import {
  DemandPartnerDynamicField,
  type DemandPartnerBidderConfig,
  type DemandPartnerBidderConfigEntry,
} from './model';
import type { DemandPartnerFormValue } from './formSchema';

export const fallbackDemandPartnerBidderConfig: DemandPartnerBidderConfig = {
  default: { visible: [], required: [], defaults: {} },
  bidders: {
    epsilon: visible(DemandPartnerDynamicField.UrlCleanup),
    freewheel: visible(DemandPartnerDynamicField.UseStickyPricing),
    jwplayer: visible(DemandPartnerDynamicField.SkipRequestsWithoutContentUrl),
    mediagrid: visible(
      DemandPartnerDynamicField.SkipRequestsWithoutContentUrl,
      DemandPartnerDynamicField.CacheParams,
    ),
    mediagrid_adapex: visible(
      DemandPartnerDynamicField.SkipRequestsWithoutContentUrl,
      DemandPartnerDynamicField.CacheParams,
    ),
    resetdigital: visible(DemandPartnerDynamicField.UseStickyPricing),
    tritondigital: {
      visible: [DemandPartnerDynamicField.AdditionalCost],
      required: [],
      defaults: { additionalCost: 0.35 },
    },
    zeta: cacheCountryPreset(),
    zeta_dx: cacheCountryPreset(),
  },
};

export function resolveDemandPartnerBidderConfig(
  bidderCode: string,
  config: DemandPartnerBidderConfig,
) {
  const bidder: DemandPartnerBidderConfigEntry = config.bidders[bidderCode] ?? {
    visible: [],
    required: [],
    defaults: {},
  };
  return {
    visible: unique([...config.default.visible, ...bidder.visible]),
    required: unique([...config.default.required, ...bidder.required]),
    defaults: { ...config.default.defaults, ...bidder.defaults },
    cacheParamsPreset: bidder.cacheParamsPreset ?? config.default.cacheParamsPreset ?? [],
  };
}

export function applyDemandPartnerBidderConfig(
  value: DemandPartnerFormValue,
  config: DemandPartnerBidderConfig,
): DemandPartnerFormValue {
  const resolved = resolveDemandPartnerBidderConfig(value.bidderCode, config);
  const next = {
    ...value,
    requiredDynamicFields: resolved.required.filter(isDemandPartnerDynamicField),
    cacheParamsVisible: resolved.visible.includes(DemandPartnerDynamicField.CacheParams),
  };

  for (const [field, defaultValue] of Object.entries(resolved.defaults)) {
    if (!(field in next) || next[field as keyof DemandPartnerFormValue] !== undefined) continue;
    Object.assign(next, { [field]: defaultValue });
  }

  if (
    next.cacheParamsVisible &&
    !value.cacheParamsEnabled &&
    resolved.cacheParamsPreset.length > 0
  ) {
    const preset = new Set(resolved.cacheParamsPreset.map((item) => item.toLowerCase()));
    Object.assign(next, {
      cacheParamsEnabled: true,
      cacheUa: preset.has('ua'),
      cacheIp: preset.has('ip'),
      cacheBundle: preset.has('bundle'),
      cacheCountry: preset.has('country'),
      cacheCity: preset.has('city'),
    });
  }
  return next;
}

function visible(...fields: DemandPartnerDynamicField[]) {
  return { visible: fields, required: [], defaults: {} };
}

function cacheCountryPreset() {
  return {
    visible: [DemandPartnerDynamicField.CacheParams],
    required: [],
    defaults: {},
    cacheParamsPreset: ['country'],
  };
}

function unique(values: string[]) {
  return [...new Set(values)];
}

function isDemandPartnerDynamicField(value: string): value is DemandPartnerDynamicField {
  return Object.values(DemandPartnerDynamicField).includes(value as DemandPartnerDynamicField);
}
